import React from 'react';
import DraggableModal from './DraggableModal';

interface BatchRestoreModalProps {
    isOpen: boolean;
    onClose: () => void;
    onRestore: () => void;
    onClearBatch: () => void;
    batchInfo: {
        status: 'deleted' | 'completed' | 'processing' | 'waiting_start' | 'unknown';
        batch_name?: string;
        batch_created_at?: string;
        batch_completed_at?: string;
        total_files?: number;
        processed_files?: number;
        message?: string;
        restorable?: boolean;
    } | null;
}

const BatchRestoreModal: React.FC<BatchRestoreModalProps> = ({
    isOpen,
    onClose,
    onRestore,
    onClearBatch,
    batchInfo
}) => {
    if (!batchInfo) return null;

    const getStatusIcon = () => {
        switch (batchInfo.status) {
            case 'deleted':
                return '🗑️';
            case 'completed':
                return batchInfo.restorable === false ? '⚠️' : '✅';
            case 'processing':
                return '⏳';
            case 'waiting_start':
                return '⏸️';
            default:
                return '❓';
        }
    };

    const getStatusText = () => {
        switch (batchInfo.status) {
            case 'deleted':
                return 'Batch removed';
            case 'completed':
                return batchInfo.restorable === false ? 'Not restorable' : 'Batch completed';
            case 'processing':
                return 'Batch in progress';
            case 'waiting_start':
                return 'Batch waiting to start';
            default:
                return 'Unknown status';
        }
    };

    const getStatusColor = () => {
        switch (batchInfo.status) {
            case 'deleted':
                return '#dc3545';
            case 'completed':
                return batchInfo.restorable === false ? '#dc3545' : '#28a745';
            case 'processing':
                return '#ffc107';
            case 'waiting_start':
                return '#17a2b8';
            default:
                return '#6c757d';
        }
    };

    const formatDate = (dateString?: string) => {
        if (!dateString) return 'Not available';
        try {
            return new Date(dateString).toLocaleString('en-GB');
        } catch {
            return dateString;
        }
    };

    const formatFileProgress = () => {
        if (!batchInfo.total_files) return 'Not available';
        const processed = batchInfo.processed_files || 0;
        return `${processed} of ${batchInfo.total_files} files`;
    };

    return (
        <DraggableModal isOpen={isOpen} onClose={onClose}>
            <div className="modal-shell">
                <h4 className="modal-handle">🔄 Previous batch found</h4>
                <div className="batch-restore-info">
                    <div className="batch-status-header">
                        <span className="status-icon">{getStatusIcon()}</span>
                        <div className="status-details">
                            <div
                                className="status-title"
                                style={{ color: getStatusColor() }}
                            >
                                {getStatusText()}
                            </div>
                            {batchInfo.batch_name && (
                                <div className="batch-name">📁 {batchInfo.batch_name}</div>
                            )}
                        </div>
                    </div>

                    <div className="batch-details">
                        <div className="detail-row">
                            <span className="detail-label">Created:</span>
                            <span className="detail-value">{formatDate(batchInfo.batch_created_at)}</span>
                        </div>

                        {batchInfo.batch_completed_at && (
                            <div className="detail-row">
                                <span className="detail-label">Completed:</span>
                                <span className="detail-value">{formatDate(batchInfo.batch_completed_at)}</span>
                            </div>
                        )}

                        <div className="detail-row">
                            <span className="detail-label">File progress:</span>
                            <span className="detail-value">{formatFileProgress()}</span>
                        </div>

                        {batchInfo.message && (
                            <div className="detail-row">
                                <span className="detail-label">Note:</span>
                                <span className="detail-value">{batchInfo.message}</span>
                            </div>
                        )}
                    </div>

                    <div className="batch-actions-info">
                        {batchInfo.status === 'processing' && batchInfo.restorable !== false && (
                            <div className="info-chip info-chip--warning">
                                ⚠️ The batch is still processing. Restoring will continue where it left off.
                            </div>
                        )}

                        {batchInfo.status === 'waiting_start' && batchInfo.restorable !== false && (
                            <div className="info-chip info-chip--info">
                                ℹ️ The batch was uploaded but not started. Restore to continue.
                            </div>
                        )}

                        {batchInfo.restorable === false && (
                            <div className="info-chip info-chip--error">
                                🗑️ This batch was cleaned up and cannot be restored. Please start a new batch.
                            </div>
                        )}

                        {batchInfo.status === 'completed' && batchInfo.restorable !== false && (
                            <div className="info-chip info-chip--success">
                                ✅ Batch completed successfully. You can restore the results.
                            </div>
                        )}

                        {batchInfo.status === 'deleted' && batchInfo.restorable !== false && (
                            <div className="info-chip info-chip--error">
                                🗑️ The batch was deleted and is no longer available.
                            </div>
                        )}

                        {batchInfo.status === 'unknown' && (
                            <div className="info-chip info-chip--warning">
                                ❓ Unable to determine batch status. It may have been removed.
                            </div>
                        )}
                    </div>
                </div>

                <div className="modal-actions">
                    {(batchInfo.status === 'processing' || batchInfo.status === 'waiting_start' || batchInfo.status === 'completed') && batchInfo.restorable !== false && (
                        <button className="modal-btn modal-btn--primary" onClick={onRestore}>
                            Restore batch
                        </button>
                    )}

                    <button
                        className="modal-btn modal-btn--danger"
                        onClick={onClearBatch}
                    >
                        Delete batch
                    </button>
                </div>
            </div>
        </DraggableModal>
    );
};

export default BatchRestoreModal;
